import styled, { css } from 'styled-components';
import Text from '@bit/knotchinc.web-react-components.text';
import { TagText } from 'components/Global/Tag/styled.js';
import { truncateByWidth, absFull } from 'helpers/styles.js';
import paidContentBadge from 'images/icons/paidContentBadge.svg';
import ownedContentBadge from 'images/icons/ownedContentBadge.svg';
import contentIcon from 'images/icons/content-piece.svg';
import videoIcon from 'images/icons/video-play.svg';
import tagIcon from 'images/icons/tag.svg';
import { CardContainer } from '@bit/knotchinc.web-react-components.image-card';

const iconMap = {
  video: videoIcon,
  article: contentIcon,
  keywords: tagIcon,
  owned: ownedContentBadge,
  paid: paidContentBadge,
};


export const StyledSharesContainer = styled.div`
  position: absolute;
  top: 7px;
  left: 0;
  color: white;
  width: 50%;
  font-weight: bold;
  font-size: 11px;
  line-height: 28px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #FBFBFC;
  transition: all .3s ease;
  white-space: nowrap;

  &:before {
    content: '';
    ${absFull(0, 'auto', 0, '-17px')};
    width: 4px;
    background: ${({ theme }) => theme.colors.palette[5]};
  }
`;

/* SINGLETON ELEMENTS */
export const StyledMenuItem = styled.div`
  display: inline-block;
  z-index: 5;
  background: rgba(31, 34, 43, 0.2);
  width: 29px;
  height: 29px;
  border-radius: 50%;
  margin: 0 2px;
  position: relative;
  padding: 9px;
  transition: all .3s ease;

  &:hover {
  background: rgba(255, 255, 255, .17);
  }

  ${({ selected }) => selected && css`
    background: ${({ theme }) => theme.colors.palette[7]};
    box-shadow: 0 0 9px rgba(0,0,0,0.25);

    &:after {
      transform: scale(1);
      opacity: 0;
    }
  `}
`;

export const StyledEntityIcon = styled.div`
  display: inline-block;
  width: 28px;
  height: 28px;
  border-radius: 100%;
  background: ${({ icon }) => `url('${icon}')`};
  background-size: contain;
  background-repeat: no-repeat;
  background-color: white;
  background-position: center center;
  overflow: hidden;
  cursor: pointer;
  transform: scale(1);
  will-change: transform;
  transition: all .1s ease;
  margin-right: 4px;
  vertical-align: middle;

  &:active {
    transform: scale()(1.05);
  }

  &:hover {
    transform: scale(1.1);
  }
`;

export const StyledContentIcon = styled.div`
  display: inline-block;
  width: 11px;
  height: 11px;
  background: ${({ type }) => `url('${iconMap[type]}')`};
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center center;
  vertical-align: top;
`;

export const StyledKeywordsIcon = styled.div`
  display: inline-block;
  width: 11px;
  height: 11px;
  background: url('${iconMap.keywords}');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center center;
  vertical-align: top;
  margin-right: 10px;
  position: relative;
`;

export const StyledAnnotationText = styled.div`
  opacity: 0;
  display: inline-block;
  transition: all .6s ease;
  font-size: 12px;
  vertical-align: top;
  text-transform: ${({ type }) => type === 'contentType' ? 'capitalize' : 'none'};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  max-width: 0;
  transition: all .3s ease;
  will-change: width;
  transform: translate3d(0, 0, 1px);
`;

export const StyledAnnotations = styled.div`
  white-space: nowrap;
  overflow: hidden;
`;

export const StyledAnnotationBlock = styled.div`
  display: inline-block;
  width: auto;
  max-width: 25px;
  transition: all .3s ease;
  will-change: width;
  transform: translate3d(0, 0, 1px);
`;

export const StyledPublishDate = styled.div`
  color: ${({ theme }) => theme.colors.palette[5]};
  font-size: 12px;
  transition: all .3s ease;
  display: inline-block;
  vertical-align: top;
`;

export const StyledContentType = styled.div`
  display: inline-block;
  font-size: 12px;
  padding-top: 10px;
  padding-left: 20px;
  line-height: 10px;
  letter-spacing: 1px;
  text-transform: capitalize;
  color: white;
  background: url('${paidContentBadge}') no-repeat 0px 8px;
  background-size: 12px 12px;

  span {
    opacity: 0;
    visibility: hidden;
    display: none;
    font-size: 12px;
    line-height: 10px;
    vertical-align: middle;
  }

  ${({ contentType }) => contentType === 'owned' && css`
    color: #E6AFC4;
    background: url('${ownedContentBadge}') no-repeat 0px 8px;
  `};
`;

export const StyledEntityLink = styled.div`
  opacity: 0;
  transition: all .3s cubic-bezier(.77,0,.18,1);
  transition-delay: .3s;
  color: white;
  text-decoration: none;
  width: 100%;

  &:hover {
    text-decoration: underline;
  }

  &:first-child {
    margin-right: 10px;
  }
`;


/* CONTAINERS */
export const StyledMenuContainer = styled.div`
  display: inline-block;
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 5;
`;

export const StyledTagsContainer = styled.div`
  color: ${({ theme }) => theme.colors.palette[5]};
  font-size: 12px;
  margin-top: 15px;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  height: 19px;
  padding-top: 4px;

  ${TagText} {
    display: inline-block;
    margin: 0 5px 0 0;

    &:hover {
      text-decoration: underline;
    }

    &::after {
      content: ',';
    }

    &:nth-last-of-type(1):after{
      content: none;
    }
  }
`;

export const StyledFloatingLogoContainer = styled.div`
  top: -25px;
  right: 24px;
  display: inline-block;
  position: absolute;

  ${StyledEntityIcon} {
    width: 50px;
    height: 50px;
    background-color: #fff;

    &:first-child {
      margin-right: -5px;
    }
  }
`;

export const StyledContentNumContainer = styled.div`
  display: block;
  height: 16px;
  line-height: 12px;
  margin-top: 12px;
`;


/* TEXT ELEMENTS */
export const StyledURL = styled.div`
  ${truncateByWidth('100%')};
  color: ${({ theme }) => theme.colors.palette[7]};
  font-size: 12px;
`;

export const StyledEntityTitle = styled(Text)`
  display: inline-block;
  max-width: 32px;
  font-size: 12px;
  line-height: 17px;
  font-style: normal;
  vertical-align: middle;
  max-height: 32px;
  transition: all .6s cubic-bezier(.77,0,.18,1);
  will-change: width;
  transform: translate3d(0, 0, 1px);
  padding: 2px 16px 2px 2px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  ${({ type }) => type === 'folder' && css`
    margin-bottom: 5px;
    width: 100%;
  `}
`;

export const StyledContentNum = styled.div`
  font-size: 12px;
  margin-left: 10px;
  display: inline-block;
  vertical-align: top;
`;

export const ContentCardContainer = styled.div`
  position: relative;
  display: inline-block;
  width: ${({ width }) => width};
  height: ${({ height }) => height};
  z-index: 1;

  ${CardContainer} {
    transition: all .3s ease;
    z-index: 5;
    opacity: 1;
  }

  ${({ isOpen }) => isOpen && css`
    ${CardContainer} {
      opacity: 0;
    }
  `};

  &:hover{
    ${StyledSharesContainer} {
      &:before {
        left: -18px;
      }
    }

    ${StyledEntityTitle} {
      opacity: 1;
      max-width: 50%;
      width: auto;
      text-overflow: ellipsis;
    }

    ${StyledEntityLink} {
      opacity: 1;
    }

    ${StyledAnnotationBlock},
    ${StyledAnnotationText} {
      max-width: 100%;
      width: auto;
      opacity: 1;
    }

    ${StyledAnnotationText} {
      padding-left: 6px;
    }
  }
`;

export const StyledContentTitle = styled.div`
  font-size: 15px;
  line-height: 21px;
  margin: 20px 0 15px 0;
`;

export const StyledFolderEntityTitle = styled.div`
  display: inline-block;
  color: ${({ theme }) => theme.colors.palette[7]};
  font-size: 10px;
  letter-spacing: 2px;
  line-height: 1.5;
  font-weight: bold;
  text-transform: uppercase;
  vertical-align: middle;
  white-space: break-spaces;
`;
